/*
 * Copyright (c) 2022 NOOGA DEVELOPMENT, All rights reserved.
 * Author Julia Romanovskaya <julia.romanovskaya@noogadev.com>
 */

import { createAction } from '../../utils/redux-utils';
import { DocumentsListDto, DocumentsListFilterParams } from '../api/documents/documentsClient.typings';
import { Notification, NOTIFICATION_TYPES } from '../../utils/utils';
import { TimeRangeFilterParams } from '../../components/RegistryWidgets/Common/TimeRangeFilter';
import { i18n } from '../translation/i18n';
import { CashboxesByTaxNumbersDto, CashboxesByTaxNumbersQueryParams } from '../api/companies/companiesClient.typings';

export enum DocumentsActionType {
    OPEN_REGISTRY_DOCUMENTS_LIST_PAGE = 'RegistryDocumentsList/ open page',
    EXIT_REGISTRY_DOCUMENTS_LIST_PAGE = 'RegistryDocumentsList/ exit page',

    FETCH_NEXT_PAGE_START = 'RegistryDocumentsList/ fetch next page start',
    FETCH_EXTENDED_DATA_START = 'RegistryDocumentsList/ fetch extended data start',
    EXTENDED_DATA_IS_COMPLETE = 'RegistryDocumentsList/ extended data is complete',
    CHANGE_FILTERS = 'RegistryDocumentsList/ change documents list filters',
    CHANGE_TIME_RANGE_FILTER_PARAMS = 'RegistryDocumentsList/ change time range filter params',
    APPLY_FILTERS = 'RegistryDocumentsList/ apply filters',

    FETCH_DOCUMENTS_PAGE_SUCCESS = 'RegistryDocumentsList/ fetch documents list page success',
    FETCH_DOCUMENTS_PAGE_ERROR = 'RegistryDocumentsList/ fetch documents list page error',
    FETCH_EXTENDED_DATA_SUCCESS = 'RegistryDocumentsList/ fetch documents list extended data success',
    FETCH_EXTENDED_DATA_ERROR = 'RegistryDocumentsList/ fetch documents list extended data error',

    DOWNLOAD_EXCEL_START = 'RegistryDocumentsList/ download documents list as excel start',
    DOWNLOAD_EXCEL_SUCCESS = 'RegistryDocumentsList/ download documents list as excel success',
    DOWNLOAD_EXCEL_ERROR = 'RegistryDocumentsList/ download documents list as excel error',

    CHANGE_CURRENT_VIEW = 'RegistryDocumentsList/ change current view',
}

export const openRegistryDocumentsListAction = () =>
    createAction(DocumentsActionType.OPEN_REGISTRY_DOCUMENTS_LIST_PAGE);

export const exitRegistryDocumentsListAction = () =>
    createAction(DocumentsActionType.EXIT_REGISTRY_DOCUMENTS_LIST_PAGE);

export const fetchNextPageStartAction = () => createAction(DocumentsActionType.FETCH_NEXT_PAGE_START);

export const fetchExtendedDataStartAction = (params: CashboxesByTaxNumbersQueryParams) =>
    createAction(DocumentsActionType.FETCH_EXTENDED_DATA_START, params);

export const extendedDataIsCompleteAction = () => createAction(DocumentsActionType.EXTENDED_DATA_IS_COMPLETE);

export const changeDocumentsListFiltersAction = (params: DocumentsListFilterParams) =>
    createAction(DocumentsActionType.CHANGE_FILTERS, params);

export const changeTimeRangeFilterParamsAction = (params: TimeRangeFilterParams) =>
    createAction(DocumentsActionType.CHANGE_TIME_RANGE_FILTER_PARAMS, params);

export const applyFilteringAction = () => createAction(DocumentsActionType.APPLY_FILTERS);

export const downloadDocumentsListAsExcelStartAction = () => createAction(DocumentsActionType.DOWNLOAD_EXCEL_START);

export const downloadDocumentsListAsExcelSuccessAction = () =>
    createAction(DocumentsActionType.DOWNLOAD_EXCEL_SUCCESS, {
        notification: {
            type: NOTIFICATION_TYPES[1],
            message: i18n.t('downloadings.fileCreationRequestAccepted'),
        },
    });

export const downloadDocumentsListAsExcelErrorAction = (notification: Notification) =>
    createAction(DocumentsActionType.DOWNLOAD_EXCEL_ERROR, { notification });

export const fetchDocumentsListPageSuccessAction = (list: DocumentsListDto) =>
    createAction(DocumentsActionType.FETCH_DOCUMENTS_PAGE_SUCCESS, {
        list,
        notification:
            list.documents.length === 0
                ? {
                      type: NOTIFICATION_TYPES[1],
                      message: i18n.t('errors.notFoundByParams'),
                  }
                : null,
    });

export const fetchDocumentsListPageErrorAction = (notification: Notification) =>
    createAction(DocumentsActionType.FETCH_DOCUMENTS_PAGE_ERROR, { notification });

export const fetchExtendedDataSuccessAction = (payload: CashboxesByTaxNumbersDto) =>
    createAction(DocumentsActionType.FETCH_EXTENDED_DATA_SUCCESS, payload);

export const fetchExtendedDataErrorAction = (notification: Notification) =>
    createAction(DocumentsActionType.FETCH_EXTENDED_DATA_ERROR, { notification });

export const changeCurrentViewAction = (isCardView: boolean) =>
    createAction(DocumentsActionType.CHANGE_CURRENT_VIEW, isCardView);

export type DocumentsAction =
    | ReturnType<typeof openRegistryDocumentsListAction>
    | ReturnType<typeof exitRegistryDocumentsListAction>
    | ReturnType<typeof fetchNextPageStartAction>
    | ReturnType<typeof fetchExtendedDataStartAction>
    | ReturnType<typeof extendedDataIsCompleteAction>
    | ReturnType<typeof changeDocumentsListFiltersAction>
    | ReturnType<typeof changeTimeRangeFilterParamsAction>
    | ReturnType<typeof applyFilteringAction>
    | ReturnType<typeof downloadDocumentsListAsExcelStartAction>
    | ReturnType<typeof downloadDocumentsListAsExcelSuccessAction>
    | ReturnType<typeof downloadDocumentsListAsExcelErrorAction>
    | ReturnType<typeof fetchDocumentsListPageSuccessAction>
    | ReturnType<typeof fetchDocumentsListPageErrorAction>
    | ReturnType<typeof fetchExtendedDataSuccessAction>
    | ReturnType<typeof fetchExtendedDataErrorAction>
    | ReturnType<typeof changeCurrentViewAction>;
